import React, { Dispatch, FC, ReactElement, ReactNode, SetStateAction } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { Order } from '../services/typings';
import { validateOrder, cancelOrder } from '../services/order';

export const useStyles = makeStyles<Theme>((theme: Theme) => ({
  container: {
    width: 'auto',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(2) * 2)]: {
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  paper: {
    marginTop: theme.spacing(1),
    overflowX: 'auto',
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px ${theme.spacing(1)}px`,
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  buttonContainer: {
    display: 'flex',
    flexFlow: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(3),
    width: 300,
  },
  table: {
    minWidth: 650,
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px ${theme.spacing(1)}px`,
    marginTop: theme.spacing(3),
  },
  error: {
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px ${theme.spacing(1)}px`,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}));

export interface Props {
  order: Order;
  updateOrders(): Promise<void>;
  setSnackBarMessage: Dispatch<SetStateAction<ReactNode>>;
}

const OrderPage: FC<Props> = ({ order, updateOrders, setSnackBarMessage }: Props): ReactElement => {
  const classes = useStyles({});
  const [actionDisabled, setActionDisabled] = React.useState(false);
  const wrapValidate = async () => {
    setActionDisabled(true);
    try {
      await validateOrder(order.id);
      await updateOrders();
    } catch (error) {
      setSnackBarMessage(error);
    } finally {
      setActionDisabled(false);
    }
  };
  const wrapCancel = async () => {
    setActionDisabled(true);
    try {
      await cancelOrder(order.id);
      await updateOrders();
    } catch (error) {
      setSnackBarMessage(error);
    } finally {
      setActionDisabled(false);
    }
  };
  return (
    <Container className={classes.container}>
      <Paper className={classes.paper}>
        <Typography variant="h4" align="center">
          Commande #{order.number}
        </Typography>
        <Typography variant="h5" align="center">
          test@foodles.co chez {order.transaction.entity}
        </Typography>
        <Typography variant="overline" align="center">
          {`en date du: ${new Date(order.transaction.created_at).toLocaleString('fr-FR', {
            timeZone: 'Europe/Paris',
          })}`}
        </Typography>
        <Typography variant="overline" align="center">
          {`total: ${(-order.transaction.amount.amount / 100).toFixed(2)} ${
            order.transaction.amount.currency
          }`}
        </Typography>
      </Paper>
      <div className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={wrapCancel}
          className={classes.button}
          disabled={actionDisabled}
        >
          Rembourser et annuler
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={wrapValidate}
          className={classes.button}
          disabled={actionDisabled}
        >
          Valider
        </Button>
      </div>
      <TableContainer component={Paper}>
        <Table className={classes.table} aria-label="products">
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell align="left">Nom</TableCell>
              <TableCell align="left">Quantité</TableCell>
              <TableCell align="right">Prix</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {order.transaction.items.map(item => (
              <TableRow key={item.name}>
                <TableCell component="th" scope="row">
                  <Avatar alt={item.name} src={item.image} />
                </TableCell>
                <TableCell component="th" scope="row" align="left" style={{ fontSize: '2rem' }}>
                  {item.name}
                </TableCell>
                <TableCell align="left" style={{ fontSize: '2rem' }}>
                  {item.quantity}
                </TableCell>
                <TableCell align="right">
                  {`${(item.saved_price_monetary_amount.amount / 100).toFixed(2)} ${
                    item.saved_price_monetary_amount.currency
                  }`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default OrderPage;
